import Link from 'next/link';
import { memo } from 'react';

import {
  formatMonthlyPrice,
  type PlanInfoType,
  type UpgradePlanModalBillingCycle,
} from '~/components/Modals/PlansModal/utils';
import { Routes } from '~/constants/routes';

export type UpgradePlanModalSidebarProps = {
  currentPlan: PlanInfoType | null;
  billingCycle: UpgradePlanModalBillingCycle;
};

export const UpgradePlanModalSidebar = memo(({ currentPlan, billingCycle }: UpgradePlanModalSidebarProps) => {
  return (
    <div className="flex flex-shrink-0 flex-col gap-8 bg-white px-6 py-6 shadow-kanban sm:rounded-t-8 lg:w-[280px] lg:rounded-l-8 lg:rounded-tr-none lg:py-18 dark:bg-gray-1">
      {currentPlan && (
        <>
          <p className="font-semibold text-24 text-gray-12">
            {currentPlan.planId === 'basic' || currentPlan.isLegacy
              ? 'Find your new plan'
              : `Upgrade from your ${currentPlan.planTitle} plan`}
          </p>
          <div className="flex flex-col gap-2">
            <p className="font-semibold text-12 text-gray-10 uppercase tracking-wide">Current plan</p>
            <div className="rounded-12 border border-gray-2 bg-gray-3 p-4">
              <p className="font-control text-32 text-gray-12">{currentPlan.planTitle}</p>
              {currentPlan.isLegacy && <p className="-mt-1 text-14 text-gray-10">(Legacy)</p>}
              <div className="mt-2 mb-4 flex items-end gap-1.5">
                <span className="font-semibold text-24 text-gray-10 leading-none">
                  {formatMonthlyPrice({ prices: currentPlan.prices, billingCycle })}
                </span>{' '}
                <div className="flex flex-col font-semibold text-10 text-gray-9">
                  <span>{currentPlan.pricingModel === 'perMember' ? 'per member per' : 'per'} month</span>
                  <span>billed {billingCycle === 'annual' ? 'annually' : 'monthly'}</span>
                </div>
              </div>
              <div className="flex flex-col gap-2 text-14">
                {currentPlan.credits && !currentPlan.isLegacy && (
                  <p className="font-semibold text-gray-12">{currentPlan.credits}</p>
                )}
                {currentPlan.options?.map((option) => (
                  <p key={option} className="text-gray-10">
                    {option}
                  </p>
                ))}
              </div>
            </div>
            {currentPlan.isLegacy && (
              <p className="text-12 text-gray-12">
                This plan is no longer available. Please contact <Link href="mailto:help@air.inc">help@air.inc</Link> to
                explore your options.
              </p>
            )}
          </div>
        </>
      )}

      <div className="flex flex-col gap-2">
        <p className="font-semibold text-12 text-gray-10 uppercase tracking-wide">How credits work</p>
        <p className="text-12 text-gray-12">
          Credits in Air are a shared monthly pool that powers storage and AI (enrichment and generations), updates as
          you work, refreshes each month, and can be topped up or upgraded when you need more.{' '}
          <Link href={Routes.creditsHelp} target="_blank" rel="noreferrer noopener" className="font-medium">
            Learn more
          </Link>
        </p>
      </div>
    </div>
  );
});

UpgradePlanModalSidebar.displayName = 'UpgradePlanModalSidebar';
